import { useState } from 'owa-react';
import { observer } from 'owa-mobx-react';
/* eslint-disable-next-line @typescript-eslint/no-restricted-imports -- (https://aka.ms/OWALintWiki)
 * Prefer Satchel for state management
 *	> 'useManagedMutation' import from 'owa-apollo-hooks' is restricted. Prefer Satchel for state management */
import { useManagedMutation } from 'owa-apollo-hooks';
import type { MailboxInfo } from 'owa-client-types';
import { lazyCreateNewFolder } from 'owa-folder-createnew';
import { lazyRenameFolder } from 'owa-folder-rename';
import { RenameFolderDocument } from 'owa-folder-rename/lib/graphql/__generated__/RenameFolderMutation.interface';
import { type CreateFolderInvoker, type CreateFolderDepth } from 'owa-folders-constants';
import type { FolderForestTreeType } from 'owa-graph-schema';
import loc, { format } from 'owa-localize';
import { lazyDismissMailFolderTextField } from 'owa-mail-folder-store';
import React from 'react';
import {
    newFolderAnnouncementText,
    renameFolderAnnouncementText,
} from './FolderOperation.locstring.json';
import FolderTextField from './FolderTextField';
import { shouldRenderNodeInEditMode } from './utils/shouldRenderNodeInEditMode';
import type { ResourceId } from 'owa-localize';

export interface FolderOperationNodeProps {
    folderId: string;
    treeType: FolderForestTreeType;
    nestDepth: number;
    operationType: string;
    originalValue?: string;
    mailboxInfo: MailboxInfo;
    ariaAnnouncementLabel?: ResourceId;
    onNewFolderTextFieldEntry?: (value: string) => void;
    onRenameFolderTextFieldEntry?: (value: string) => void;
}

const defaultExport: React.FunctionComponent<FolderOperationNodeProps> = observer(
    function FolderOperationNode(props: FolderOperationNodeProps) {
        const [folderUpdateText, setFolderUpdateText] = useState('');
        const [renameFolderMutation] = useManagedMutation(RenameFolderDocument);

        const onNewFolderTextFieldEntry = React.useCallback(
            (value: string) => {
                lazyDismissMailFolderTextField.importAndExecute();

                const parentId = props.folderId;
                lazyCreateNewFolder.importAndExecute(
                    {
                        parentId,
                        displayName: value,
                        mailboxInfo: props.mailboxInfo,
                    },
                    {
                        invoker: 'operationnode',
                        depth: props.nestDepth > 0 ? 'subfolder' : 'parent',
                    }
                );
                setFolderUpdateText(format(loc(newFolderAnnouncementText), value));
            },
            /* eslint-disable-next-line owa-custom-rules/no-unused-react-set-state -- (https://aka.ms/OWALintWiki)
             * DO NOT COPY-PASTE!, add a justification or fix the issue
             *	> React setState functions from useState should only be used by direct invocation, setState(), not passed or used as a value or used in hook's array dependencies. */
            [setFolderUpdateText, props.folderId, props.nestDepth]
        );

        const onRenameFolderTextFieldEntry = React.useCallback(
            (value: string, folderId?: string) => {
                lazyDismissMailFolderTextField.importAndExecute();

                const oldFolderName = props.originalValue || '';
                const newFolderName = (value || '').trim(); // remove trailing whitespace

                if (!folderId || !newFolderName || newFolderName === oldFolderName) {
                    // Folder name didn't change. So nothing more
                    return;
                }

                lazyRenameFolder.importAndExecute(
                    renameFolderMutation,
                    {
                        id: folderId,
                        newDisplayName: newFolderName,
                        mailboxInfo: props.mailboxInfo,
                    },
                    oldFolderName
                );

                const announcementText =
                    props.ariaAnnouncementLabel || renameFolderAnnouncementText;
                setFolderUpdateText(format(loc(announcementText), value));
            },
            /* eslint-disable-next-line owa-custom-rules/no-unused-react-set-state -- (https://aka.ms/OWALintWiki)
             * DO NOT COPY-PASTE!, add a justification or fix the issue
             *	> React setState functions from useState should only be used by direct invocation, setState(), not passed or used as a value or used in hook's array dependencies. */
            [setFolderUpdateText]
        );

        let nodeToReturn;
        const folderTextInputStyles = {
            fieldGroup: {
                border: 0,
                '&:after': {
                    borderRadius: '4px',
                },
            },
        };

        if (
            props.operationType == 'new' ||
            (props.operationType == 'newNode' &&
                shouldRenderNodeInEditMode(
                    props.folderId,
                    'new',
                    props.mailboxInfo.mailboxSmtpAddress,
                    props.treeType
                ))
        ) {
            nodeToReturn = (
                <FolderTextField
                    nestDepth={props.nestDepth}
                    onEntry={props.onNewFolderTextFieldEntry ?? onNewFolderTextFieldEntry}
                    styles={folderTextInputStyles}
                />
            );
        } else if (props.operationType == 'rename') {
            nodeToReturn = (
                <FolderTextField
                    nestDepth={props.nestDepth}
                    onEntry={props.onRenameFolderTextFieldEntry ?? onRenameFolderTextFieldEntry}
                    defaultValue={props.originalValue}
                    folderId={props.folderId}
                    styles={folderTextInputStyles}
                />
            );
        }

        if (nodeToReturn) {
            return (
                <>
                    {nodeToReturn}
                    <span className="screenReaderOnly" aria-live="assertive" aria-atomic="true">
                        {folderUpdateText}
                    </span>
                </>
            );
        }

        return null;
    },
    'FolderOperationNode'
);
export default defaultExport;
